var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
// External dependencies
import { useContext, useEffect, useState, useCallback, useRef, memo, } from 'react';
import { Box, Paper, Typography, Grid, TextField, IconButton, Button, Tooltip, } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Clear, AttachFile, StopCircle } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import 'katex/dist/katex.min.css';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
// Internal dependencies
import { appStyles } from '../App.styles';
import { getAuthHeader, UserContext } from '../auth';
import { axiosClient } from '../axios';
import { chatStyles } from './Chat.styles';
import { LOG_LEVELS, SERVICES, ENDPOINTS, CHAT_CONTEXT, CLIENT_ENDPOINTS } from '../constants';
import { BrandVoiceIconSG, SendChatIconSG, ChatPromptIconSG, RewriteIconSG, VoiceRecordIconSG, } from '../components';
import BrandVoiceDialog from './brandVoiceDialog';
import { InfobaseDialog } from '../infobase';
import { AI_MODELS } from './constants';
import { Prompts } from '../prompt';
/**
 * Chat Message Input component
 * @param props props
 * @returns Chat Message Input element
 */
export const ChatMessageInput = memo(function ({ chat, setChat, sendMessage, waiting, outputLanguage, showEditor, setMessageBlockHeight, }) {
    var _a, _b, _c;
    // Get user credential
    const user = useContext(UserContext);
    const navigate = useNavigate();
    // Get translation function
    const { t, i18n } = useTranslation();
    /**
     * Send Chat Message
     */
    const sendChatMessage = () => __awaiter(this, void 0, void 0, function* () {
        try {
            mediaRecorder && mediaRecorder.stop();
            setIsVoiceRecording(false);
            // Clone message, attachFiles
            // Prevent side effect
            const cloneMessage = `${formik.values.message}`;
            const cloneAttachFiles = attachFiles.map((attachFile) => (Object.assign({}, attachFile)));
            const metadata = { imageSize: chat === null || chat === void 0 ? void 0 : chat.imageSize };
            // Reset message, attachFiles
            formik.setFieldValue('message', '');
            setAttachFiles([]);
            yield sendMessage(cloneMessage, metadata, selectedBrandVoiceOption.content, cloneAttachFiles, CHAT_CONTEXT.CHAT);
        }
        catch (err) {
            // Save error to the system log
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `ChatMessageInput, handleMessageInputKeyUp, ${err === null || err === void 0 ? void 0 : err.message}`,
                metadata: {
                    env: process.env.ENV,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                },
            });
            toast.error(t('Something went wrong. We are figuring it out.'));
        }
        ;
    });
    const formik = useFormik({
        initialValues: {
            /** Chat input message */ message: '',
        },
        validateOnChange: false,
        onSubmit: (values) => __awaiter(this, void 0, void 0, function* () {
            try {
                yield sendChatMessage();
            }
            catch (err) {
                // Save error to the system log
                axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                    level: LOG_LEVELS.ERROR,
                    logMessage: `ChatMessageInput, onSubmit, ${err === null || err === void 0 ? void 0 : err.message}`,
                    metadata: {
                        env: process.env.ENV,
                        userId: user === null || user === void 0 ? void 0 : user.uid,
                        email: user === null || user === void 0 ? void 0 : user.email,
                        userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                    },
                });
                toast.error(t('Something went wrong. We are figuring it out.'));
            }
            ;
        }),
    });
    const [/** Shift key up time */ shiftKeyUpTimeStamp, setShiftKeyUpTimeStamp] = useState(0);
    /**
     * Handle Shift & Enter input key up together will send out message
     * @param event Keyboard Event
     * @returns isNotReleaseTogether
     */
    const avoidShiftEnterKeyUpSendOutMsg = (event) => {
        //check if the shift and enter key are release within the buffer time 700ms
        const timeStampDiffBuffer = 700;
        const isReleaseTogther = event.key === 'Enter' && (event.timeStamp - shiftKeyUpTimeStamp <= timeStampDiffBuffer);
        return !isReleaseTogther;
    };
    /**
     * Handle message input key up
     * @param {KeyboardEvent<HTMLInputElement>} event
     * @returns {void}
     */
    const handleMessageInputKeyUp = (event) => __awaiter(this, void 0, void 0, function* () {
        try {
            if (event.key === 'Shift' && event.type === "keyup") {
                setShiftKeyUpTimeStamp(event.timeStamp);
            }
            if (event.key === 'Enter' && !event.shiftKey && avoidShiftEnterKeyUpSendOutMsg(event)) {
                yield sendChatMessage();
            }
            ;
        }
        catch (err) {
            // Save error to the system log
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `ChatMessageInput, handleMessageInputKeyUp, ${err === null || err === void 0 ? void 0 : err.message}`,
                metadata: {
                    env: process.env.ENV,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                },
            });
        }
        ;
    });
    // Messages Block Ref
    const messageBlockRef = useRef(null);
    // Update Message block height whenever the height changed
    useEffect(() => {
        try {
            if (messageBlockRef) {
                setMessageBlockHeight(messageBlockRef.current.clientHeight);
            }
            ;
        }
        catch (err) {
            // Save error to the system log
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `ChatMessageInput, useEffect, ${err === null || err === void 0 ? void 0 : err.message}`,
                metadata: {
                    env: process.env.ENV,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                },
            });
        }
        ;
    }, [(_a = messageBlockRef === null || messageBlockRef === void 0 ? void 0 : messageBlockRef.current) === null || _a === void 0 ? void 0 : _a.clientHeight]);
    useEffect(() => {
        try {
            /**
            * Handle window resize
            */
            function handleWindowResize() {
                setMessageBlockHeight(messageBlockRef.current.clientHeight);
            }
            ;
            // Add window resize listener
            window.addEventListener('resize', handleWindowResize);
            // Remove window resize listener
            return () => {
                window.removeEventListener('resize', handleWindowResize);
            };
        }
        catch (err) {
            // Save error to the system log
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `ChatMessageInput, useEffect, ${err === null || err === void 0 ? void 0 : err.message}`,
                metadata: {
                    env: process.env.ENV,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                },
            });
        }
        ;
    }, []);
    const [/** boolean */ infobaseOpen, setInfobaseOpen] = useState(false);
    /**
     * Handle infobase open
     */
    const handleInfobaseOpen = () => {
        setInfobaseOpen(true);
    };
    /**
     * Handle Infobase close
     */
    const handleInfobaseClose = useCallback(() => {
        setInfobaseOpen(false);
    }, []);
    // Fetch and update document when user credential changed
    useEffect(() => {
        /**
         * Get list of brand voices
         * @returns {Promise<void>}
         */
        const getBrandVoices = () => __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            try {
                if (!user) {
                    return;
                }
                // Get auth header
                const authHeader = yield getAuthHeader(user);
                // Get brand voices results
                const res = yield axiosClient.get(`${process.env.SERVER_URL}${ENDPOINTS.CHAT}${ENDPOINTS.GET_BRAND_VOICES}`, { headers: Object.assign(Object.assign({}, authHeader), { 'Accept-Language': i18n.language }) });
                // Extract brand voices from the resulst
                const brandVoices = ((_b = (_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.brandVoices) || [];
                // Create brand voice options for the Brand voice select input
                const brandVoiceOptions = brandVoices.map((brandVoice) => {
                    return {
                        title: brandVoice.name,
                        value: brandVoice._id,
                        content: brandVoice.content,
                    };
                });
                // Update the brand voice options
                setBrandVoiceOptions([defaultBrandVoiceOption, ...brandVoiceOptions]);
            }
            catch (err) {
                axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                    level: LOG_LEVELS.ERROR,
                    logMessage: `ChatMessageInput, getBrandvoices, ${err === null || err === void 0 ? void 0 : err.message}`,
                    metadata: {
                        env: process.env.ENV,
                        service: SERVICES.AI_CHAT,
                        userId: user === null || user === void 0 ? void 0 : user.uid,
                        email: user === null || user === void 0 ? void 0 : user.email,
                        userAgent: navigator.userAgent,
                    },
                });
            }
            ;
        });
        try {
            if (user) {
                getBrandVoices();
            }
        }
        catch (err) {
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `ChatMessageInput, useEffect, getBrandVoices, ${err.message}`,
                metadata: {
                    env: process.env.ENV,
                    service: SERVICES.AI_CHAT,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator.userAgent,
                },
            });
        }
        ;
    }, [user]);
    // Default brand voice
    const defaultBrandVoice = { name: 'No Brand Voice', content: '', _id: '0' };
    // Default brand voice option for the select input
    const defaultBrandVoiceOption = { title: 'No Brand Voice', content: '', value: '0' };
    // List of brand voice options for the select input
    const [brandVoiceOptions, setBrandVoiceOptions] = useState([defaultBrandVoiceOption]);
    // The selected brand voice option for the select input
    const [selectedBrandVoiceOption, setSelectedBrandVoiceOption] = useState(defaultBrandVoiceOption);
    // Brand Voice open/close status
    const [brandVoiceOpen, setBrandVoiceOpen] = useState(false);
    useEffect(() => {
        // Get selected brand voice index from the brand voices list
        const selectedBrandVoiceIndex = brandVoiceOptions.findIndex((brandVoiceOption) => ((brandVoiceOption || '0').value.toString() === ((chat === null || chat === void 0 ? void 0 : chat.brandVoiceId) || '0').toString()));
        // Update the selected brand voice if it's existed in the brand voices list
        if (selectedBrandVoiceIndex >= 0) {
            setSelectedBrandVoiceOption(Object.assign({}, brandVoiceOptions[selectedBrandVoiceIndex]));
        }
    }, [chat === null || chat === void 0 ? void 0 : chat.brandVoiceId, brandVoiceOptions]);
    /**
     * Handle Brand Voice close
     */
    const handlebrandVoiceClose = () => {
        setBrandVoiceOpen(false);
    };
    /**
     * Handle Brand Voice open
     */
    const handlebrandVoiceOpen = () => {
        setBrandVoiceOpen(true);
    };
    /**
     * Handle save brand voice
     * @param {any} data
     */
    const handleSaveBrandVoice = (data) => {
        try {
            chat.brandVoiceId = data === null || data === void 0 ? void 0 : data.value;
            setChat(Object.assign({}, chat));
            setBrandVoiceOpen(false);
            setSelectedBrandVoiceOption(data);
        }
        catch (err) {
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `ChatMessageInput, handleSaveBrandVoice, ${err.message}`,
                metadata: {
                    env: process.env.ENV,
                    service: SERVICES.AI_CHAT,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator.userAgent,
                },
            });
            toast.error(t('Something went wrong. We are figuring it out.'));
        }
        ;
    };
    const [
    /** Improve button loading status */ improvePromptLoading, 
    /** improvePromptLoading setter */ setImprovePromptLoading,] = useState(false);
    /**
     * Handle improve prompt button click
     * @returns Promise of success/fail status
     */
    const handleImprovePromptClick = () => __awaiter(this, void 0, void 0, function* () {
        var _d, _e, _f, _g, _h;
        try {
            if (!user) {
                navigate(CLIENT_ENDPOINTS.SIGN_IN);
                return false;
            }
            // Set status for Improve Prompt button is Loading
            setImprovePromptLoading(true);
            // Get autheHeader
            const authHeader = yield getAuthHeader(user);
            // Get headers
            const headers = Object.assign(Object.assign({}, authHeader), { 'Accept-Language': i18n.language });
            // Call Improve chat prompt API
            const res = yield axiosClient.post(`${process.env.SERVER_URL}${ENDPOINTS.CHAT}${ENDPOINTS.IMPROVE_PROMPT}`, { description: formik.values.message, outputLanguage }, { headers });
            // Extract files
            const choices = (_e = (_d = res === null || res === void 0 ? void 0 : res.data) === null || _d === void 0 ? void 0 : _d.data) === null || _e === void 0 ? void 0 : _e.choices;
            if (choices) {
                formik.setFieldValue('message', (choices[0].message.content || '').trim());
            }
            // Set status for Improve Prompt button is done
            setImprovePromptLoading(false);
            return true;
        }
        catch (err) {
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `ChatMessageInput, handleImprovePromptClick, ${err === null || err === void 0 ? void 0 : err.message}`,
                metadata: {
                    env: process.env.ENV,
                    service: SERVICES.AI_CHAT,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator.userAgent,
                },
            });
            // Check if the error is from axios
            if ((_f = err === null || err === void 0 ? void 0 : err.response) === null || _f === void 0 ? void 0 : _f.status) {
                // If yes, notify user with the server error message
                toast.error((_h = (_g = err === null || err === void 0 ? void 0 : err.response) === null || _g === void 0 ? void 0 : _g.data) === null || _h === void 0 ? void 0 : _h.message);
                // Set status for Improve Prompt button is done
                setImprovePromptLoading(false);
                return false;
            }
            ;
            toast.error(t('Something went wrong. We are figuring it out.'));
            // Set status for Improve Prompt button is done
            setImprovePromptLoading(false);
            return false;
        }
        ;
    });
    const /** Ai Model */ aiModel = user ? ((chat === null || chat === void 0 ? void 0 : chat.aiModel) || AI_MODELS.aiSearch.modelValue) : AI_MODELS.incognito.modelValue;
    // Prompts open/close status
    const [promptsOpen, setPromptsOpen] = useState(false);
    /**
     * Handle prompts open
     */
    const handlePromptsOpen = () => {
        if (!user) {
            navigate(CLIENT_ENDPOINTS.SIGN_IN);
            return;
        }
        setPromptsOpen(true);
    };
    /**
     * Handle Prompts close
     */
    const handlePromptsClose = useCallback(() => {
        setPromptsOpen(false);
    }, []);
    // Attach files
    const [attachFiles, setAttachFiles] = useState([]);
    /**
     * Handle attach file removing
     * @param url
     */
    const handleAttachFileRemove = (url) => {
        try {
            // Remove delete item
            const detachedFileIdx = attachFiles.findIndex((attachFile) => ((attachFile === null || attachFile === void 0 ? void 0 : attachFile.url) === url));
            attachFiles.splice(detachedFileIdx, 1);
            setAttachFiles([...attachFiles]);
        }
        catch (err) {
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `ChatMessageInput, handleAttachFileRemove, ${err.message}`,
                metadata: {
                    env: process.env.ENV,
                    service: SERVICES.DOCUMENTS,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                },
            });
            toast.error(t('Something went wrong. We are figuring it out.'));
        }
        ;
    };
    const handlePromptClick = useCallback((prompt) => {
        formik.setFieldValue('message', t(prompt.promptContent) || '');
    }, []);
    const /** Model metadata */ modelMetadata = AI_MODELS[aiModel];
    /** Support Attach Files feature or not */
    const isAttachFilesSupport = ((modelMetadata === null || modelMetadata === void 0 ? void 0 : modelMetadata.attachFilesTypes) || []).length > 0;
    /** Support Brand Voice or not */
    const isBrandVoiceSupport = modelMetadata === null || modelMetadata === void 0 ? void 0 : modelMetadata.isBrandVoiceSupport;
    const [mediaRecorder, setMediaRecorder] = useState(undefined);
    const [audioURL, setAudioURL] = useState('');
    const [isVoiceRecording, setIsVoiceRecording] = useState(false);
    const [audioRecordedMessage, setAudioRecordedMessage] = useState('');
    /**
     * Handle Voice Recorder start
     */
    const handleVoiceRecorderStart = () => __awaiter(this, void 0, void 0, function* () {
        try {
            if (!user) {
                // Redirect to the SignIn screen if the user is not logged in
                navigate(CLIENT_ENDPOINTS.SIGN_IN);
                return;
            }
            setIsVoiceRecording(true);
            // Get microphone access
            const stream = yield navigator.mediaDevices.getUserMedia({ audio: true });
            // Wrap the stream into the recorder
            const mediaRecorder = new MediaRecorder(stream);
            const audioChunks = [];
            // Handle stream
            mediaRecorder.addEventListener("dataavailable", event => {
                audioChunks.push(event.data);
            });
            // Handle when user stop recording
            mediaRecorder.addEventListener("stop", () => __awaiter(this, void 0, void 0, function* () {
                var _j, _k;
                const audioBlob = new Blob(audioChunks, { type: "audio/webm" });
                const /** Auth headers */ authHeader = yield getAuthHeader(user);
                const /** Request headers */ headers = Object.assign(Object.assign({}, authHeader), { "Content-Type": "multipart/form-data", 'Accept-Language': i18n.language });
                const /** Http form data */ formData = new FormData();
                // Add file to form data
                formData.append('file', audioBlob, 'audio.webm');
                // Call upload file api
                const res = yield axiosClient.post(`${process.env.SERVER_URL}${ENDPOINTS.CHAT}${ENDPOINTS.CONVERT_VOICE_TO_TEXT}`, formData, { headers });
                // Get the text and update the message
                const text = ((_k = (_j = res === null || res === void 0 ? void 0 : res.data) === null || _j === void 0 ? void 0 : _j.data) === null || _k === void 0 ? void 0 : _k.text) || '';
                setAudioRecordedMessage(text);
            }));
            // Start recording
            setMediaRecorder(mediaRecorder);
            mediaRecorder.start();
        }
        catch (err) {
            setIsVoiceRecording(false);
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `ChatMessageInput, handleVoiceRecorderStart, ${err.message}`,
                metadata: {
                    env: process.env.ENV,
                    service: SERVICES.AI_CHAT,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                },
            });
        }
        ;
    });
    useEffect(() => {
        var _a, _b;
        if (((_b = (_a = formik === null || formik === void 0 ? void 0 : formik.values) === null || _a === void 0 ? void 0 : _a.message) === null || _b === void 0 ? void 0 : _b.length) === 0) {
            formik.setFieldValue('message', audioRecordedMessage);
        }
    }, [audioRecordedMessage]);
    /**
     * Handle Voice Recorder stop
     */
    const handleVoiceRecorderStop = () => __awaiter(this, void 0, void 0, function* () {
        try {
            if (!user) {
                // Redirect to the SignIn screen if the user is not logged in
                navigate(CLIENT_ENDPOINTS.SIGN_IN);
                return;
            }
            setIsVoiceRecording(false);
            mediaRecorder && mediaRecorder.stop();
        }
        catch (err) {
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `ChatMessageInput, handleVoiceRecorderStop, ${err.message}`,
                metadata: {
                    env: process.env.ENV,
                    service: SERVICES.AI_CHAT,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                },
            });
            toast.error(t('Something went wrong. We are figuring it out.'));
        }
        ;
    });
    useEffect(() => {
        // Stop audio recording if user type any text
        mediaRecorder && mediaRecorder.stop();
        if (isVoiceRecording) {
            setIsVoiceRecording(false);
        }
    }, [((_c = (_b = formik === null || formik === void 0 ? void 0 : formik.values) === null || _b === void 0 ? void 0 : _b.message) === null || _c === void 0 ? void 0 : _c.length) > 0]);
    return (_jsxs(_Fragment, { children: [_jsx(Box, Object.assign({ component: "form", onSubmit: formik.handleSubmit }, { children: _jsxs(Paper
                // Infobase paper
                , Object.assign({ 
                    // Infobase paper
                    ref: messageBlockRef, sx: {
                        background: '#FFFFFF',
                        border: '1px solid #006B54',
                        boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.3), 0px 1px 3px 1px rgba(0, 0, 0, 0.15)',
                        borderRadius: '1rem',
                        padding: '0.75rem 1rem',
                        position: 'absolute',
                        bottom: '0',
                        width: '100%',
                        boxSizing: 'border-box',
                    } }, { children: [_jsx(Box, { children: attachFiles.map((attachFile) => {
                                const fileName = attachFile === null || attachFile === void 0 ? void 0 : attachFile.name;
                                return (_jsxs(Grid, Object.assign({ container: true, sx: {
                                        width: 'auto',
                                        display: 'inline-flex',
                                        alignItems: 'stretch',
                                        border: '1px solid #707974',
                                        borderRadius: '1.875rem',
                                        padding: '0 0 0 0.5rem',
                                        margin: '0 0.5rem 0.5rem 0',
                                    } }, { children: [_jsx(Grid, Object.assign({ item: true, sx: {
                                                display: 'flex',
                                                alignItems: 'center',
                                                overflow: 'hidden',
                                                maxWidth: '150px',
                                            } }, { children: _jsx(Typography, Object.assign({ sx: {
                                                    overflow: 'hidden',
                                                    whiteSpace: 'nowrap',
                                                    textOverflow: 'ellipsis',
                                                } }, { children: fileName })) })), _jsx(Grid, Object.assign({ item: true }, { children: _jsx(IconButton, Object.assign({ "aria-label": "Close prompt button", onClick: () => { handleAttachFileRemove(attachFile.url); } }, { children: _jsx(Clear, { sx: { color: '#1C1B1F' } }) })) }))] }), attachFile === null || attachFile === void 0 ? void 0 : attachFile._id));
                            }) }), _jsx(TextField, { name: 'message', value: formik.values.message, multiline: true, autoComplete: "off", autoFocus: true, variant: "standard", sx: { width: '100%' }, InputProps: {
                                disableUnderline: true,
                                style: {
                                    padding: '0',
                                }
                            }, inputProps: {
                                style: Object.assign(Object.assign({}, appStyles.text16), { color: '#3F4945', padding: '0 0 0.5rem', maxHeight: '30vh', overflow: 'auto' }),
                            }, placeholder: `${t('Ask AI anything and get answers')}`, onChange: formik.handleChange, onKeyUp: handleMessageInputKeyUp }), _jsxs(Grid, Object.assign({ container: true, columnSpacing: 1, sx: { justifyContent: 'space-between' } }, { children: [_jsx(Grid, Object.assign({ item: true, xs: true, sx: { display: 'flex', alignItems: 'center' } }, { children: _jsxs(Grid, Object.assign({ container: true, columnSpacing: 1 }, { children: [_jsx(Grid, Object.assign({ item: true, sx: { display: 'flex', alignItems: 'center' } }, { children: _jsx(Tooltip, Object.assign({ title: !isAttachFilesSupport
                                                        ? t('The selected model does not support this feature')
                                                        : t('Upload files or images'), placement: 'top', slotProps: {
                                                        tooltip: {
                                                            sx: Object.assign({}, chatStyles.tooltip)
                                                        }
                                                    } }, { children: _jsxs(Box, { children: [_jsx(Button, Object.assign({ "aria-label": "Attach button", variant: "contained", color: 'sixth', disabled: !isAttachFilesSupport ? true : false, onClick: handleInfobaseOpen, startIcon: _jsx(AttachFile, { sx: {
                                                                        width: '18px',
                                                                        height: '18px',
                                                                        color: 'blackLight.main',
                                                                    } }), sx: Object.assign(Object.assign({}, chatStyles.textBtn), { color: 'blackLight.main', display: {
                                                                        xl: 'inline-flex',
                                                                        md: showEditor ? 'none' : 'inline-flex',
                                                                        xs: 'none',
                                                                    } }) }, { children: t('Attach') })), _jsx(IconButton, Object.assign({ "aria-label": "Attach button for mobile", disabled: !isAttachFilesSupport ? true : false, onClick: handleInfobaseOpen, sx: {
                                                                    width: '40px',
                                                                    height: '40px',
                                                                    display: {
                                                                        xl: 'none',
                                                                        md: showEditor ? 'inline-flex' : 'none',
                                                                        xs: 'inline-flex',
                                                                    },
                                                                } }, { children: _jsx(AttachFile, { sx: {
                                                                        width: '18px',
                                                                        height: '18px',
                                                                        color: 'blackLight.main',
                                                                    } }) }))] }) })) })), _jsx(Grid, Object.assign({ item: true, sx: { display: 'flex', alignItems: 'center' } }, { children: _jsx(Tooltip, Object.assign({ title: t('Choose clear instructions to get improved responses'), placement: 'top', slotProps: {
                                                        tooltip: {
                                                            sx: Object.assign({}, chatStyles.tooltip)
                                                        }
                                                    } }, { children: _jsxs(Box, { children: [_jsx(Button, Object.assign({ "aria-label": "Prompts button", variant: "contained", color: 'sixth', onClick: handlePromptsOpen, startIcon: _jsx(ChatPromptIconSG, { sx: {
                                                                        width: '1.125rem',
                                                                        height: '1.125rem',
                                                                        color: 'blackLight.main',
                                                                    } }), sx: Object.assign(Object.assign({}, chatStyles.textBtn), { color: 'blackLight.main', display: {
                                                                        lg: 'inline-flex',
                                                                        md: showEditor ? 'none' : 'inline-flex',
                                                                        sm: 'inline-flex',
                                                                        xs: 'none',
                                                                    } }) }, { children: t('Prompts') })), _jsx(IconButton, Object.assign({ "aria-label": "Prompts button for mobile", onClick: handlePromptsOpen, sx: {
                                                                    width: '40px',
                                                                    height: '40px',
                                                                    display: {
                                                                        lg: 'none',
                                                                        md: showEditor ? 'inline-flex' : 'none',
                                                                        sm: 'none',
                                                                        xs: 'inline-flex',
                                                                    },
                                                                } }, { children: _jsx(ChatPromptIconSG, { sx: {
                                                                        width: '1.125rem',
                                                                        height: '1.125rem',
                                                                        color: 'blackLight.main',
                                                                    } }) }))] }) })) })), _jsx(Grid, Object.assign({ item: true, xs: true, sx: { display: 'flex', maxWidth: '480px', alignItems: 'center' } }, { children: _jsx(Tooltip, Object.assign({ title: !isBrandVoiceSupport ? t('The selected model does not support this feature') : t(`Set AI's writing style to match brand`), placement: 'top', slotProps: {
                                                        tooltip: {
                                                            sx: Object.assign({}, chatStyles.tooltip)
                                                        }
                                                    } }, { children: _jsx(Box, { children: _jsx(Button, Object.assign({ "aria-label": "Brand Voice button", variant: "contained", color: 'sixth', disabled: !isBrandVoiceSupport ? true : false, onClick: handlebrandVoiceOpen, startIcon: _jsx(BrandVoiceIconSG, { styles: {
                                                                    width: '1.125rem',
                                                                    height: '1.125rem',
                                                                    color: 'blackDark.main',
                                                                } }), sx: {
                                                                borderRadius: '0.5rem',
                                                                fontWeight: '500',
                                                                boxShadow: 'none',
                                                                textTransform: 'none',
                                                                padding: {
                                                                    lg: '0.375rem 1rem 0.375rem 0.75rem',
                                                                    xs: '0.375rem',
                                                                },
                                                                display: 'inline-flex',
                                                                minWidth: '0',
                                                            } }, { children: _jsx(Grid, Object.assign({ container: true, sx: {
                                                                    width: '100%',
                                                                    maxWidth: {
                                                                        xl: '147px',
                                                                        md: showEditor ? '64px' : '147px',
                                                                        sm: showEditor ? '32px' : '100px',
                                                                        xs: '32px',
                                                                    },
                                                                } }, { children: _jsx(Grid, Object.assign({ item: true, xs: true, sx: {
                                                                        overflow: 'hidden',
                                                                        width: '100%',
                                                                    } }, { children: _jsx(Typography, Object.assign({ sx: Object.assign(Object.assign({}, appStyles.text14), { overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', fontWeight: '500', color: !isBrandVoiceSupport ? 'text.diabled' : 'blackLight.main' }) }, { children: t(selectedBrandVoiceOption.title) })) })) })) })) }) })) })), _jsx(Grid, Object.assign({ item: true, sx: { display: 'flex', alignItems: 'center' } }, { children: _jsx(Tooltip, Object.assign({ title: t(`Improve questions for better answers`), placement: 'top', slotProps: {
                                                        tooltip: {
                                                            sx: Object.assign({}, chatStyles.tooltip)
                                                        }
                                                    } }, { children: _jsx(Box, { children: _jsx(LoadingButton, Object.assign({ "aria-label": "Improve Prompt button", variant: "text", color: 'green', loading: improvePromptLoading, onClick: handleImprovePromptClick, disabled: (formik.values.message.length > 0 && !waiting) ? false : true, startIcon: _jsx(RewriteIconSG, { sx: {
                                                                    fontSize: '1.125rem',
                                                                    color: 'blackDark.main',
                                                                } }), sx: Object.assign(Object.assign({}, chatStyles.textBtn), { overflow: 'hidden', maxWidth: {
                                                                    xl: 'calc(100%)',
                                                                    md: showEditor ? '64px' : '100%',
                                                                    sm: showEditor ? '64px' : '96px',
                                                                    xs: '64px',
                                                                } }) }, { children: _jsx(Typography, Object.assign({ sx: {
                                                                    textOverflow: 'ellipsis',
                                                                    overflow: 'hidden',
                                                                    width: 'calc(100%)',
                                                                    whiteSpace: 'nowrap',
                                                                } }, { children: t('Improve') })) })) }) })) }))] })) })), _jsxs(Grid, Object.assign({ item: true }, { children: [_jsxs(Box, Object.assign({ sx: { display: (formik.values.message.length === 0) ? 'inline-flex' : 'none', } }, { children: [_jsx(IconButton, Object.assign({ "aria-label": "Record icon", color: "blackDark", sx: {
                                                        textTransform: 'none',
                                                        display: !isVoiceRecording ? 'inline-flex' : 'none',
                                                    }, onClick: handleVoiceRecorderStart }, { children: _jsx(VoiceRecordIconSG, { sx: { fontSize: '1.5rem' } }) })), _jsx(IconButton, Object.assign({ "aria-label": "Stop Record icon", color: "blackDark", sx: {
                                                        textTransform: 'none',
                                                        display: isVoiceRecording ? 'inline-flex' : 'none',
                                                    }, onClick: handleVoiceRecorderStop }, { children: _jsx(StopCircle, { sx: { fontSize: '1.5rem' } }) }))] })), _jsx(IconButton
                                        // Send message button
                                        , Object.assign({ "aria-label": "Send message button", color: "green", sx: {
                                                padding: '0',
                                                textTransform: 'none',
                                                display: (formik.values.message.length > 0 && !waiting) ? 'inline-block' : 'none',
                                            }, type: "submit", disabled: (formik.values.message.length > 0 && !waiting) ? false : true }, { children: _jsx(SendChatIconSG, { sx: { fontSize: '2.5rem' } }) }))] }))] }))] })) })), _jsx(BrandVoiceDialog, { open: brandVoiceOpen, onClose: handlebrandVoiceClose, option: brandVoiceOptions, value: selectedBrandVoiceOption, onSave: handleSaveBrandVoice }), _jsx(Prompts, { handlePromptClick: handlePromptClick, handlePromptsClose: handlePromptsClose, open: promptsOpen }), _jsx(InfobaseDialog, { aiModel: aiModel, open: infobaseOpen, handleInfobaseClose: handleInfobaseClose, attachFiles: attachFiles, setAttachFiles: setAttachFiles })] }));
});
